import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { QueryKeys } from "@/lib/types";
import {
  createAuthenticatedApi,
  EMAIL_ALREADY_IN_USE_MESSAGE,
  getApiErrorMessage,
  throwIfResultFailed,
} from "@/lib/utils";
import {
  type CreateEmployeeRequest,
  PaymentType,
  TeachersApi,
  type UpdateTeacherRequest,
  UsersApi,
} from "@/openapi/generated";

export interface CreateEmployeeInput {
  request: CreateEmployeeRequest;
  teacherDetails: Omit<UpdateTeacherRequest, "teacherId"> | null;
}

export interface CreateEmployeeResult {
  userId: number;
  teacherDetailsSaved: boolean;
}

export const useEmployeeCreate = (
  toast: ReturnType<typeof import("@/components/feedback").useToast>,
) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const { mutate, isPending, isSuccess, isError } = useMutation({
    mutationFn: async ({
      request,
      teacherDetails,
    }: CreateEmployeeInput): Promise<CreateEmployeeResult> => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }

      const users = createAuthenticatedApi(UsersApi, accessToken);
      const result = await users.apiUserEmployeePost({
        createEmployeeRequest: request,
      });
      throwIfResultFailed(result, "Failed to create employee");

      const userId = Number(result.data?.userId);
      if (!Number.isFinite(userId) || userId <= 0) {
        throw new Error("Failed to create employee");
      }

      if (!teacherDetails) {
        return { userId, teacherDetailsSaved: true };
      }

      try {
        const teachers = createAuthenticatedApi(TeachersApi, accessToken);
        await teachers.apiTeachersPut({
          updateTeacherRequest: {
            ...teacherDetails,
            teacherId: userId,
            paymentType: teacherDetails.paymentType ?? PaymentType.PayByHour,
          },
        });
        return { userId, teacherDetailsSaved: true };
      } catch {
        return { userId, teacherDetailsSaved: false };
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.Teachers] });
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.UsersScrollGet],
      });
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.UsersByRoleGet],
      });
      toast({
        title: "Employee created",
        description: "Employee created successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: async (error: unknown) => {
      const errorMessage = await getApiErrorMessage(
        error,
        "Failed to create employee",
        { conflictFallback: EMAIL_ALREADY_IN_USE_MESSAGE },
      );

      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutate, isPending, isError, isSuccess };
};
